import dayjs from 'dayjs';

export const fancyTimeFormat = (duration) => {
    // Hours, minutes and seconds
    var hrs = ~~(duration / 3600);
    var mins = ~~((duration % 3600) / 60);
    var secs = ~~duration % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    var ret = "";

    if (hrs > 0) {
        ret += "" + hrs + ":" + (mins < 10 ? "0" : "");
    }

    ret += "" + mins + ":" + (secs < 10 ? "0" : "");
    ret += "" + secs;
    return ret;
}

export const fancyTimeFormatMinutesOrSeconds = (duration) => {
    // if duration is less than 60, return string in seconds.
    if (duration < 60) {
        return ~~duration + "s";
    }

    // else return string in minutes without seconds.
    var mins = ~~((duration % 3600) / 60);
    return mins + "m";
}


export const fancyTimeFormatWithHMS = (duration) => {
    // Hours, minutes and seconds
    var hrs = ~~(duration / 3600);
    var mins = ~~((duration % 3600) / 60);
    var secs = ~~duration % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    var ret = "";

    if (hrs > 0) {
        ret += "" + hrs + "h";
    }
    if (mins > 0) {
        ret += " " + mins + "m";
    }
    if (secs > 0 && hrs === 0) {
        ret += " " + secs + "s";
    }
    return ret.trim();
}

export const fancyTimeFormatWithHM = (duration) => {
    const normalizedDuration = Number(duration);
    if (!Number.isFinite(normalizedDuration) || normalizedDuration <= 0) {
        return '';
    }

    if (normalizedDuration < 60) {
        return ~~normalizedDuration + "s"
    }
    // Hours, minutes and seconds
    var hrs = ~~(normalizedDuration / 3600);
    var mins = ~~((normalizedDuration % 3600) / 60);

    // Output like "1:01" or "4:03:59" or "123:03:59"
    var ret = "";

    if (hrs === 1) {
        ret += "" + hrs + "h";
    } else if (hrs > 1) {
        ret += "" + hrs + "h";
    }

    if (mins === 1) {
        ret += " " + mins + "m";
    } else if (mins > 1) {
        ret += " " + mins + "m";
    }
    return ret.trim();
};

const MINIMUM_RESUME_LISTEN_SECONDS = 30;
const MINIMUM_RESUME_PROGRESS_FRACTION = 0.01;

export const isMeaningfullyStarted = (lastListenSeconds, totalTimeSeconds) => {
    const normalizedLastListenSeconds = Number(lastListenSeconds);
    const normalizedTotalTimeSeconds = Number(totalTimeSeconds);

    if (
        !Number.isFinite(normalizedLastListenSeconds) ||
        normalizedLastListenSeconds < 0 ||
        !Number.isFinite(normalizedTotalTimeSeconds) ||
        normalizedTotalTimeSeconds <= 0
    ) {
        return false;
    }

    const minimumVisibleResumeSeconds = Math.max(
        MINIMUM_RESUME_LISTEN_SECONDS,
        normalizedTotalTimeSeconds * MINIMUM_RESUME_PROGRESS_FRACTION,
    );
    return normalizedLastListenSeconds >= minimumVisibleResumeSeconds;
};

export const getDateString = (publishedDate) => {
    return dayjs(publishedDate).format('MMM D, YYYY');
};

export const isTooOld = (dateMillis, tooOldSecondsLimit) => {
    if (!dateMillis) {
        return true;
    }
    const diffSeconds = (Date.now() - dateMillis) / 1000;
    return diffSeconds > tooOldSecondsLimit;
};


const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const RELATIVE_DATE_UNITS = [
    [365 * ONE_DAY_MS, 'y'],
    [30 * ONE_DAY_MS, 'mo'],
    [7 * ONE_DAY_MS, 'w'],
    [ONE_DAY_MS, 'd'],
    [60 * 60 * 1000, 'h'],
    [60 * 1000, 'm'],
];

// Use elapsed time, rather than calendar boundaries, consistently across clients.
const formatRelativeAge = (elapsedMs) => {
    const unit = RELATIVE_DATE_UNITS.find(([durationMs]) => elapsedMs >= durationMs);
    if (!unit) {
        return 'just now';
    }
    const [durationMs, suffix] = unit;
    return `${Math.floor(elapsedMs / durationMs)}${suffix} ago`;
};

// Recent row dates use compact elapsed time; older and future dates stay absolute.
export const formatPublishedDate = (date) => {
    if (!date) {
        return '';
    }

    const publishedDate = new Date(date);
    if (Number.isNaN(publishedDate.getTime())) {
        return '';
    }

    const now = new Date();
    const diffInMs = now - publishedDate;
    if (!Number.isFinite(diffInMs)) {
        return '';
    }

    if (diffInMs >= 0 && diffInMs < 7 * ONE_DAY_MS) {
        return formatRelativeAge(diffInMs);
    }
    return formatShortDate(publishedDate, publishedDate.getFullYear() !== now.getFullYear());
};

const startOfLocalDay = (date) => (
    new Date(date.getFullYear(), date.getMonth(), date.getDate())
);

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const addLocalDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

const startOfLocalWeek = (date) => {
    const result = startOfLocalDay(date);
    result.setDate(result.getDate() - result.getDay());
    return result;
};

const startOfLocalMonth = (date) => (
    new Date(date.getFullYear(), date.getMonth(), 1)
);

const startOfLocalYear = (date) => (
    new Date(date.getFullYear(), 0, 1)
);

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
});
const SHORT_DATE_WITH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
});

const formatShortDate = (date, includeYear = false) => {
    // Read the current local calendar date without pinning a cached formatter to
    // the timezone that was active when this module loaded.
    const calendarDate = new Date(0);
    calendarDate.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    const formatter = includeYear ? SHORT_DATE_WITH_YEAR_FORMATTER : SHORT_DATE_FORMATTER;
    return formatter.format(calendarDate);
};

export const formatPreviewInfoPublishedDate = (date) => {
    if (!date) {
        return '';
    }

    const publishedDate = new Date(date);
    if (Number.isNaN(publishedDate.getTime())) {
        return '';
    }

    const now = new Date();
    if (publishedDate > now) {
        return formatShortDate(publishedDate, true);
    }

    return formatRelativeAge(now - publishedDate);
};

export const groupTrackInfosByPublishedDate = (trackInfos, now = new Date()) => {
    if (!Array.isArray(trackInfos)) {
        return [];
    }

    const today = startOfLocalDay(now);
    const yesterday = addLocalDays(today, -1);
    const startOfThisWeek = startOfLocalWeek(now);
    const startOfThisMonth = startOfLocalMonth(now);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const startOfThisYear = startOfLocalYear(now);
    const groupsById = {};
    const months = new Set();
    const years = new Set();

    const addToGroup = (id, title, trackInfo) => {
        if (!groupsById[id]) {
            groupsById[id] = {
                id,
                title,
                count: 0,
                trackInfos: [],
            };
        }
        groupsById[id].count += 1;
        groupsById[id].trackInfos.push(trackInfo);
    };

    trackInfos.forEach((trackInfo) => {
        if (!trackInfo || !trackInfo.publishedDate) {
            return;
        }

        const publishedDate = new Date(trackInfo.publishedDate);
        if (Number.isNaN(publishedDate.getTime())) {
            return;
        }

        const publishedDay = startOfLocalDay(publishedDate);
        if (publishedDay.getTime() === today.getTime()) {
            addToGroup('today', 'today', trackInfo);
        } else if (publishedDay.getTime() === yesterday.getTime()) {
            addToGroup('yesterday', 'Yesterday', trackInfo);
        } else if (publishedDate >= startOfThisWeek) {
            addToGroup('this-week', 'This week', trackInfo);
        } else if (publishedDate >= startOfThisMonth) {
            addToGroup('this-month', 'This month', trackInfo);
        } else if (publishedDate >= startOfLastMonth) {
            addToGroup('last-month', 'Last month', trackInfo);
        } else if (publishedDate >= startOfThisYear) {
            const month = publishedDate.getMonth();
            months.add(month);
            addToGroup(`month-${month}`, MONTH_NAMES[month], trackInfo);
        } else {
            const year = publishedDate.getFullYear();
            years.add(year);
            addToGroup(`year-${year}`, String(year), trackInfo);
        }
    });

    const groupOrder = ['today', 'yesterday', 'this-week', 'this-month', 'last-month'];
    const orderedGroups = groupOrder
        .map((id) => groupsById[id])
        .filter(Boolean);

    Array.from(months)
        .sort((a, b) => b - a)
        .forEach((month) => {
            const group = groupsById[`month-${month}`];
            if (group) {
                orderedGroups.push(group);
            }
        });

    Array.from(years)
        .sort((a, b) => b - a)
        .forEach((year) => {
            const group = groupsById[`year-${year}`];
            if (group) {
                orderedGroups.push(group);
            }
        });

    return orderedGroups.map((group) => ({
        ...group,
        title: group.id === 'today'
            ? `${group.count} ${group.count === 1 ? 'episode' : 'episodes'} today`
            : group.title,
    }));
};


export const getDaysPassed = (date) => {
  const now = new Date();
  const pastDate = new Date(date);
  
  // Calculate difference in milliseconds
  const diffInMs = now - pastDate;
  
  // Convert milliseconds to days (1000ms * 60s * 60m * 24h)
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
};
